// react
import { ReactNode } from 'react';

// tanstack
import { useNavigate } from '@tanstack/react-router';

// material ui icons
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

// material ui
import { Box, IconButton, Typography } from '@mui/material';

interface PageHeaderProps {
  title: string;
  /** Navigation target for the back button. Defaults to going back in router history. */
  to?: string | number;
  /** Optional nodes rendered on the right side of the header. */
  children?: ReactNode;
  disableArrow?: boolean;
  description?: string;
}

const PageHeader = ({
  title,
  description,
  to = '..',
  children,
  disableArrow = true,
}: PageHeaderProps) => {
  const navigate = useNavigate();

  const handleBack = () => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    navigate({ to: to as any });
  };

  return (
    <Box mb={1} display="flex" alignItems="center" gap={1}>
      {!disableArrow && (
        <IconButton
          onClick={handleBack}
          data-testid="page-header-back-btn"
          size="small"
          sx={{
            color: 'text.secondary',
            '&:hover': {
              backgroundColor: 'transparent',
              color: 'primary.main',
            },
            '&:hover .arrow-icon': {
              transform: 'translateX(-4px)',
              transition: 'transform 0.2s ease',
            },
          }}
        >
          <ArrowBackIcon className="arrow-icon" fontSize="small" />
        </IconButton>
      )}
      <Box>
        <Typography variant="h6" fontWeight={600}>
          {title}
        </Typography>
        {description && (
          <Typography variant="body2" color="text.secondary">
            {description}
          </Typography>
        )}
      </Box>
      {children && (
        <Box ml="auto" display="flex" alignItems="center" gap={1}>
          {children}
        </Box>
      )}
    </Box>
  );
};

export default PageHeader;
